import { useCallback, useMemo } from "react";
import { WorkoutAlertInputType, WorkoutAlertType } from "@WahooFitness/cloud-client-types";
import {
  useCloudContext,
  useConfigContext,
  useGuardedUpdater,
} from "@WahooFitness/wahoo-offline-mfe";
import { WorkoutAlerts } from "@WahooFitness/cloud-client-ts";
import { useCloudWorkoutProfilesSource } from "./useCloudWorkoutProfilesSource";
import { WSMWorkoutAlert } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { IWorkoutAlertSource } from "./useWorkoutAlertsSource";
import { t } from "@lingui/macro";

/**
 * This hook is a little janky because we are adapting the cloud calls to look like the
 * WSM calls so we can use a feature flag to switch between the two.
 */
export const useCloudWorkoutAlertsSource = (
  workoutProfileExternalId: string,
  enabled: boolean
): IWorkoutAlertSource => {
  const { update, updateInProgress } = useGuardedUpdater();
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const {
    workoutProfiles,
    workoutProfilesAreLoading: workoutProfilesAreLoadingInternal,
    workoutProfilesError: workoutAlertsError,
    reloadWorkoutProfiles: reloadWorkoutAlerts,
  } = useCloudWorkoutProfilesSource(enabled);

  const workoutAlertClient = useMemo(() => getCloudClient(WorkoutAlerts), [getCloudClient]);

  const workoutProfile = useMemo(() => {
    return workoutProfiles?.find((p) => p.external_id === workoutProfileExternalId);
  }, [workoutProfiles, workoutProfileExternalId]);

  const makeWsmWorkoutAlert = useCallback(
    (alert: WorkoutAlertType): WSMWorkoutAlert => {
      return {
        ...alert,
        external_id: alert.external_id ?? `${alert.id}`,
        "#dbId": alert.id,
        "#deleted": false,
        token: alert.external_id ?? `${alert.id}`,
        "#workoutProfileToken": workoutProfileExternalId,
      };
    },
    [workoutProfileExternalId]
  );

  const workoutAlerts = useMemo(() => {
    return workoutProfile?.workout_alerts.map(makeWsmWorkoutAlert);
  }, [makeWsmWorkoutAlert, workoutProfile?.workout_alerts]);

  const wsmToCloudAlertMap = useMemo(() => {
    const map: Record<string, number> = {};
    workoutAlerts?.forEach((a) => {
      map[a.external_id ?? `${a.id}`] = a.id;
    });
    return map;
  }, [workoutAlerts]);

  const createWorkoutAlert = useCallback(
    async (alert: WorkoutAlertInputType) => {
      return update(
        async () => {
          if (!workoutProfile) {
            throw new Error("Workout profile not found");
          }
          const result = await workoutAlertClient.post(workoutProfile.id, wahooToken, alert);
          return makeWsmWorkoutAlert(result);
        },
        {
          errorMessage: t`Failed to create workout alert. Please check your network and try again.`,
        }
      );
    },
    [makeWsmWorkoutAlert, update, wahooToken, workoutAlertClient, workoutProfile]
  );

  const editWorkoutAlert = useCallback(
    async (workoutAlertExternalId: string, edits: WorkoutAlertInputType) => {
      return update(
        async () => {
          const cloudAlertId = wsmToCloudAlertMap[workoutAlertExternalId];
          if (cloudAlertId === undefined) {
            throw new Error("Workout alert not found");
          }
          if (!workoutProfile) {
            throw new Error("Workout profile not found");
          }
          const result = await workoutAlertClient.put(
            workoutProfile.id,
            cloudAlertId,
            wahooToken,
            edits
          );
          return makeWsmWorkoutAlert(result);
        },
        {
          errorMessage: t`Something went wrong editing the alert. Please check your network and try again.`,
        }
      );
    },
    [
      makeWsmWorkoutAlert,
      update,
      wahooToken,
      workoutAlertClient,
      workoutProfile,
      wsmToCloudAlertMap,
    ]
  );

  const deleteWorkoutAlert = useCallback(
    async (workoutAlertExternalId: string) => {
      return update(
        async () => {
          const cloudAlertId = wsmToCloudAlertMap[workoutAlertExternalId];
          if (cloudAlertId === undefined) {
            throw new Error("Workout alert not found");
          }
          if (!workoutProfile) {
            throw new Error("Workout profile not found");
          }
          const result = await workoutAlertClient.delete(
            workoutProfile.id,
            cloudAlertId,
            wahooToken
          );
          return !!result.success;
        },
        {
          errorMessage: t`Something went wrong deleting the alert. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutAlertClient, workoutProfile, wsmToCloudAlertMap]
  );

  const workoutAlertsAreLoading = useMemo(() => {
    return workoutProfilesAreLoadingInternal || updateInProgress;
  }, [updateInProgress, workoutProfilesAreLoadingInternal]);

  return {
    workoutAlerts,
    workoutAlertsAreLoading,
    workoutAlertsError,
    reloadWorkoutAlerts,
    createWorkoutAlert,
    editWorkoutAlert,
    deleteWorkoutAlert,
  };
};
